import { useEffect, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer,
} from "recharts";
import {
  Sparkles, TrendingUp, ArrowRight, Award, Target, ScrollText,
} from "lucide-react";

const STARS = Array.from({ length: 32 }, () => {
  const top = Math.random() * 100;
  const left = Math.random() * 100;
  return {
    id: `res-${top.toFixed(3)}-${left.toFixed(3)}`,
    top, left,
    size: Math.random() * 2.2 + 1,
    delay: Math.random() * 4,
    duration: 3 + Math.random() * 4,
  };
});

function StarField() {
  return (
    <div className="starfield" aria-hidden>
      {STARS.map((s) => (
        <span key={s.id} className="star" style={{
          top: `${s.top}%`, left: `${s.left}%`, width: `${s.size}px`, height: `${s.size}px`,
          animationDelay: `${s.delay}s`, animationDuration: `${s.duration}s`,
        }} />
      ))}
    </div>
  );
}

const LEVEL_COLORS = {
  Aligned: "text-emerald-300",
  Emerging: "text-amber-300",
  Exploring: "text-amber-300",
  Preparing: "text-stone-300",
};

export default function Results() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  // Mount-only fetch. `api` is a stable module import; setters are stable React refs.
  useEffect(() => {
    (async () => {
      try {
        const { data: d } = await api.get("/my-results");
        setData(d);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Complete the assessment first.");
      }
    })();
  }, []);

  if (err) {
    return (
      <div className="min-h-screen bg-[#F9F8F6]">
        <Nav />
        <div className="max-w-xl mx-auto px-6 py-16">
          <div className="overline">Not yet</div>
          <h1 className="font-serif-display text-4xl text-[#1E1B4B] mt-2">Your results are waiting.</h1>
          <p className="mt-4 text-stone-600">{err}</p>
          <Link
            to="/assessment"
            data-testid="results-take-assessment"
            className="btn-pill inline-flex mt-8 text-white text-sm px-5 py-2.5"
            style={{ background: "#1E1B4B" }}
          >
            Take the Assessment
          </Link>
        </div>
      </div>
    );
  }

  if (!data) return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />
      <div className="p-12 text-stone-500">Reading your results…</div>
    </div>
  );

  const radar = Object.entries(data.dimensions).map(([dim, score]) => ({ dim, score }));
  const catEntries = Object.entries(data.categories);
  const dateStr = data.submitted_at
    ? new Date(data.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <Nav />

      {/* Celestial hero */}
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6 }}
        className="relative overflow-hidden"
        style={{ background: "#12103A", color: "white" }}
      >
        <StarField />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14 relative">
          <div className="devanagari text-amber-300 text-sm">आपका मूल्यांकन</div>
          <div className="overline mt-2" style={{ color: "#FCD34D" }}>My Assessment Results</div>
          <h1 className="font-serif-display text-4xl lg:text-6xl mt-3 leading-tight">
            The mirror is set. Here is what it reflects.
          </h1>
          <p className="mt-4 text-stone-300 max-w-2xl">
            {data.analysis.split("**").map((chunk, idx) => {
              const stableKey = `analysis-${idx}-${chunk.slice(0, 16)}`;
              return idx % 2 === 1
                ? <strong key={stableKey} className="text-amber-300">{chunk}</strong>
                : <span key={stableKey}>{chunk}</span>;
            })}
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="border border-white/10 rounded-2xl p-6 bg-white/5" data-testid="results-overall-card">
              <div className="overline" style={{ color: "#FCD34D" }}>Overall Score</div>
              <div className="flex items-baseline gap-2 mt-2">
                <div className="font-serif-display text-5xl">{data.overall}</div>
                <div className="text-lg text-stone-400">/ 100</div>
              </div>
              <div className={`text-sm mt-2 ${LEVEL_COLORS[data.level] || "text-amber-300"}`}>
                {data.level}
              </div>
            </div>

            <div className="border border-white/10 rounded-2xl p-6 bg-white/5">
              <div className="overline" style={{ color: "#FCD34D" }}>Strongest ground</div>
              <div className="font-serif-display text-2xl mt-2 leading-tight">{data.top_category}</div>
              <div className="text-xs text-stone-400 mt-2">Build the partnership here.</div>
            </div>

            <div className="border border-white/10 rounded-2xl p-6 bg-white/5">
              <div className="overline" style={{ color: "#FCD34D" }}>Growth edge</div>
              <div className="font-serif-display text-2xl mt-2 leading-tight">{data.low_category}</div>
              <div className="text-xs text-stone-400 mt-2">Where conscious work moves the needle.</div>
            </div>
          </div>

          {dateStr && (
            <div className="mt-6 text-xs text-stone-400">
              Submitted on {dateStr} · Based on {data.answered_count} of {data.total_questions} answers
            </div>
          )}
        </div>
      </motion.div>

      {/* Category breakdown */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Category bars */}
          <div className="border border-stone-200 rounded-2xl bg-white p-7" data-testid="results-categories-card">
            <div className="overline text-amber-800">Category breakdown</div>
            <h2 className="font-serif-display text-2xl text-[#1E1B4B] mt-2">Where you sit, honestly.</h2>
            <div className="mt-6 space-y-5">
              {catEntries.map(([cat, score]) => (
                <div key={cat} data-testid={`results-cat-${cat.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
                  <div className="flex items-baseline justify-between text-sm">
                    <span className="text-[#1E1B4B] font-medium">{cat}</span>
                    <span className="font-serif-display text-xl text-[#1E1B4B]">{score}%</span>
                  </div>
                  <div className="mt-2 h-2.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full transition-all duration-700"
                      style={{
                        width: `${score}%`,
                        background: score >= 75 ? "#10B981" : score >= 55 ? "#D97706" : "#78716C",
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-6 pt-5 border-t border-stone-100 text-xs text-stone-500 leading-relaxed">
              Categories roll up from your 8 psychological dimensions. Every partnership rests on all three — the goal isn&apos;t a perfect score, it&apos;s honest awareness.
            </div>
          </div>

          {/* Radar chart of 8 dimensions */}
          <div className="border border-stone-200 rounded-2xl bg-white p-4" data-testid="results-radar-card">
            <div className="overline px-3 pt-3">Dimension Radar</div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radar}>
                  <PolarGrid stroke="#E7E5E4" />
                  <PolarAngleAxis dataKey="dim" tick={{ fill: "#57534E", fontSize: 10 }} />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: "#A8A29E", fontSize: 9 }} />
                  <Radar name="You" dataKey="score" stroke="#D97706" fill="#D97706" fillOpacity={0.3} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
            <div className="px-3 pb-3 text-xs text-stone-500">
              Eight psychological dimensions from your 18-question Marriage Intelligence Assessment.
            </div>
          </div>
        </div>

        {/* Personalised recommendations */}
        <div className="mt-10">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <div>
              <div className="overline">Your next steps</div>
              <h2 className="font-serif-display text-3xl text-[#1E1B4B] mt-2">Personalised recommendations.</h2>
            </div>
            <div className="text-xs text-stone-500 uppercase tracking-widest">
              {data.recommendations.length} actions · ranked by impact
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="results-recommendations">
            {data.recommendations.map((rec, idx) => {
              const icons = [Award, Target, Sparkles, TrendingUp, ArrowRight];
              const Icon = icons[idx % icons.length];
              return (
                <motion.div
                  key={rec.title}
                  initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
                  className="border border-stone-200 rounded-2xl bg-white p-6 flex gap-4"
                  data-testid={`results-rec-${idx}`}
                >
                  <div className="w-11 h-11 rounded-full grid place-items-center shrink-0 bg-amber-50 border border-amber-200">
                    <Icon size={18} className="text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-display text-lg text-[#1E1B4B] leading-snug">{rec.title}</div>
                    <p className="text-sm text-stone-600 mt-2 leading-relaxed">{rec.description}</p>
                    <Link
                      to={rec.cta_link}
                      data-testid={`results-rec-cta-${idx}`}
                      className="inline-flex items-center gap-1 text-sm text-amber-700 mt-3 font-medium hover:text-amber-800"
                    >
                      {rec.cta_label} <ArrowRight size={14} />
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Founder note (K. Singh branding) */}
        <div
          className="mt-12 rounded-2xl p-8 relative overflow-hidden"
          style={{ background: "#12103A", color: "white" }}
          data-testid="results-founder-note"
        >
          <StarField />
          <div className="relative flex items-start gap-5 max-w-3xl">
            <div className="w-12 h-12 rounded-full grid place-items-center shrink-0 bg-amber-500 text-[#12103A] font-serif-display text-xl">
              KS
            </div>
            <div>
              <div className="flex items-center gap-2">
                <ScrollText className="text-amber-300" size={16} />
                <div className="overline" style={{ color: "#FCD34D" }}>A note from the founder</div>
              </div>
              <p className="mt-3 font-serif-display text-lg leading-relaxed italic text-stone-100">
                &ldquo;{data.founder_note}&rdquo;
              </p>
              <div className="mt-4 text-sm">
                <span className="font-medium text-amber-300">{data.founder_name}</span>
                <span className="text-stone-400"> · {data.founder_title}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA row */}
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/matches"
            data-testid="results-cta-matches"
            className="btn-pill inline-flex items-center justify-center gap-2 text-white text-sm px-6 py-3"
            style={{ background: "#1E1B4B" }}
          >
            See my curated matches <ArrowRight size={14} />
          </Link>
          <Link
            to="/readiness"
            data-testid="results-cta-readiness"
            className="btn-pill inline-flex items-center justify-center gap-2 border border-stone-300 text-[#1E1B4B] text-sm px-6 py-3 hover:bg-white"
          >
            See my Readiness Score
          </Link>
        </div>
      </div>
    </div>
  );
}
